import { WorkerProfileField, IProfileFieldConstraintsMap } from 'types/profileCustomFields';
import { shouldShowStateForCountry } from './addressValidationUtils';
import { extractNationalNumber } from './phoneUtils';
import store from 'modules/state-mgmt/store';

/**
 * Enum for skippable till type - mirrors backend enum
 */
export enum SkippableTillType {
  NotSet = 0,
  ConsentForm = 1,
  BadgePrinting = 2,
  BadgeActivation = 3,
}

/**
 * Array of all valid skippable types for easy checking
 */
const VALID_SKIPPABLE_TYPES = [
  SkippableTillType.ConsentForm,
  SkippableTillType.BadgePrinting,
  SkippableTillType.BadgeActivation,
];

/**
 * Phone-related WorkerProfileFields that need special empty check handling
 */
const PHONE_WORKER_PROFILE_FIELDS = [
  WorkerProfileField.mobilePhoneNumber,
  WorkerProfileField.alternateContactMobilePhoneNumber,
  WorkerProfileField.emergencyContactMobilePhone,
];

/**
 * Helper to check if a WorkerProfileField is phone-related
 */
const isPhoneWorkerProfileField = (field: WorkerProfileField): boolean => {
  return PHONE_WORKER_PROFILE_FIELDS.includes(field);
};

/**
 * Converts SkippableTillType enum to human-readable text
 * @param skippableTill - The enum value
 * @returns Human-readable string
 */
export const getSkippableTillDisplayText = (skippableTill: number): string => {
  switch (skippableTill) {
    case SkippableTillType.ConsentForm:
      return 'Consent Form';
    case SkippableTillType.BadgePrinting:
      return 'Badge Printing';
    case SkippableTillType.BadgeActivation:
      return 'Badge Activation';
    default:
      return 'Not Set';
  }
};

/**
 * Converts SkippableTillType enum to translated human-readable text
 * @param skippableTill - The enum value
 * @param t - Translation function
 * @returns Translated human-readable string
 */
export const getSkippableTillDisplayTextTranslated = (skippableTill: number, t: (key: string) => string): string => {
  switch (skippableTill) {
    case SkippableTillType.ConsentForm:
      return t('personalInformation.skippable.skippableTill.consentForm');
    case SkippableTillType.BadgePrinting:
      return t('personalInformation.skippable.skippableTill.badgePrinting');
    case SkippableTillType.BadgeActivation:
      return t('personalInformation.skippable.skippableTill.badgeActivation');
    default:
      return t('personalInformation.skippable.skippableTill.notSet');
  }
};

/**
 * Determines if a field should be treated as mandatory based on current state
 * @param field - The profile field to check
 * @param constraintsMap - Map of field constraints from profile configuration
 * @param hasReachedConsentForm - Whether user has reached consent form step
 * @returns true if field should be enforced as mandatory
 */
export const shouldEnforceFieldAsMandatory = (
  field: WorkerProfileField,
  constraintsMap: IProfileFieldConstraintsMap,
  hasReachedConsentForm: boolean,
): boolean => {
  const constraints = constraintsMap[field];

  if (!constraints || !constraints.isVisible) {
    return false;
  }

  // If field is not mandatory at all, never enforce
  if (!constraints.isMandatory) {
    return false;
  }

  // If field is mandatory but not skippable, always enforce
  if (!constraints.isSkippable) {
    return true;
  }

  // If field is skippable, check the skippableTill value
  if (VALID_SKIPPABLE_TYPES.includes(constraints.skippableTill)) {
    // Only enforce ConsentForm skippable fields when hasReachedConsentForm is true
    // Badge Printing/Activation fields should NOT be enforced at this stage
    if (constraints.skippableTill === SkippableTillType.ConsentForm) {
      return hasReachedConsentForm;
    }

    // Badge Printing and Badge Activation fields remain optional at ConsentForm stage
    if (
      constraints.skippableTill === SkippableTillType.BadgePrinting ||
      constraints.skippableTill === SkippableTillType.BadgeActivation
    ) {
      // These fields will be enforced at their respective stages (not implemented yet)
      return false;
    }
  }

  // For any other skippable configuration (or no skippableTill set), enforce normally
  return true;
};

/**
 * Checks if a field should show the "required but skippable" indicator
 * @param field - The profile field to check
 * @param constraintsMap - Map of field constraints from profile configuration
 * @param hasReachedConsentForm - Whether user has reached consent form step
 * @param currentValue - Current value of the field
 * @param isPhoneValue - Whether the currentValue is a phone number that needs special handling
 * @returns true if should show skippable indicator
 */
export const shouldShowSkippableIndicator = (
  field: WorkerProfileField,
  constraintsMap: IProfileFieldConstraintsMap,
  hasReachedConsentForm: boolean,
  currentValue: any,
  isPhoneValue: boolean = false,
): boolean => {
  const constraints = constraintsMap[field];

  if (!constraints || !constraints.isVisible || !constraints.isMandatory) {
    return false;
  }

  // Don't show if field is not skippable at all
  if (!constraints.isSkippable) {
    return false;
  }

  // Check if field is skippable for any of the three types
  const hasSkippableType = VALID_SKIPPABLE_TYPES.includes(constraints.skippableTill);

  if (!hasSkippableType) {
    return false;
  }

  // If user has reached consent form:
  // - ConsentForm skippable fields should NOT show indicator (they're now mandatory)
  // - Badge Printing/Activation fields SHOULD still show indicator (they remain skippable)
  if (hasReachedConsentForm) {
    if (constraints.skippableTill === SkippableTillType.ConsentForm) {
      return false; // Hide indicator for ConsentForm fields
    }
    // Continue to show indicator for Badge fields
  }

  // Don't show if field has a value
  let hasValue = currentValue !== null && currentValue !== undefined && currentValue !== '';

  // Special handling for arrays (like government document files) - only for non-phone fields
  if (!isPhoneValue && hasValue && Array.isArray(currentValue) && currentValue.length === 0) {
    hasValue = false;
  }

  if (hasValue) {
    return false;
  }

  return true;
};

/**
 * Gets a list of skipped fields that should trigger a warning modal
 * Filters fields based on whether user has reached consent form:
 * - Before consent form: Returns all empty skippable fields
 * - After consent form: Returns only Badge Printing/Activation empty skippable fields
 *
 * @param formValues - Current form values
 * @param constraintsMap - Map of field constraints
 * @param hasReachedConsentForm - Whether user has reached consent form
 * @param fieldDisplayNames - Map of field to display names for UI
 * @param formFieldMap - Map of WorkerProfileField to form field names
 * @returns Array of skipped fields with display names and skippable till info
 */
export const getSkippedFields = (
  formValues: Record<string, any>,
  constraintsMap: IProfileFieldConstraintsMap,
  hasReachedConsentForm: boolean,
  fieldDisplayNames: Partial<Record<WorkerProfileField, string>>,
  formFieldMap: Partial<Record<WorkerProfileField, string>>,
): { field: WorkerProfileField; displayName: string; skippableTill: number }[] => {
  const skippedFields: { field: WorkerProfileField; displayName: string; skippableTill: number }[] = [];

  Object.values(WorkerProfileField).forEach(field => {
    const constraints = constraintsMap[field];

    if (!constraints || !constraints.isVisible || !constraints.isMandatory) {
      return;
    }

    // Only check skippable fields that have any of the three skippable types
    const hasSkippableType = VALID_SKIPPABLE_TYPES.includes(constraints.skippableTill);

    if (!constraints.isSkippable || !hasSkippableType) {
      return;
    }

    // Filter based on hasReachedConsentForm:
    // - If false: Check all skippable fields
    // - If true: Only check Badge Printing/Activation fields (ConsentForm fields will be validated)
    if (hasReachedConsentForm) {
      // Only include Badge Printing and Badge Activation fields
      if (
        constraints.skippableTill !== SkippableTillType.BadgePrinting &&
        constraints.skippableTill !== SkippableTillType.BadgeActivation
      ) {
        return;
      }
    }

    // Find corresponding form field value
    const formFieldName = formFieldMap[field];
    if (!formFieldName) {
      return;
    }

    // Special case: State field should only be required for countries that need it (US, CA, MX)
    // Only skip if a country IS selected AND it doesn't require states
    if (field === WorkerProfileField.homeAddressStateProvince) {
      const countryId = formValues['country']; // Country field in ContactInformation form
      if (countryId) {
        // Country is selected - check if it requires states
        const state = store.getState();
        const language = state.app?.languageCode || 'en';
        const countries = state.cache?.countries?.[language] || [];
        const selectedCountry = countries.find((c: any) => c.id === countryId);

        // If country doesn't require states (like Puerto Rico), skip checking this field
        if (!shouldShowStateForCountry(selectedCountry?.code)) {
          return;
        }
      }
      // If no country selected, continue checking state (it will be empty and flagged as skipped)
    }

    const value = formValues[formFieldName];

    // Special handling for phone number fields - check if they have actual digits
    const isPhoneField = isPhoneWorkerProfileField(field);

    let isEmpty: boolean;
    if (isPhoneField) {
      // For phone fields, use extractNationalNumber to check if there's actual input
      const nationalNumber = extractNationalNumber(value);
      isEmpty = !nationalNumber || nationalNumber.trim() === '';
    } else {
      isEmpty = value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    }

    if (isEmpty) {
      skippedFields.push({
        field,
        displayName: fieldDisplayNames[field] || field.toString(),
        skippableTill: constraints.skippableTill || 0,
      });
    }
  });

  return skippedFields;
};
